/**
 * Doc 06's property grammar, as the cards the player actually sees.
 *
 * The rule the doc is strict about: each property is introduced with a codex
 * popup BEFORE it ever appears combined, and the popup fires on first pull
 * whatever the order — because the pull order is the player's (doc 14). So the
 * carrier list is DERIVED from the shoal's roster rather than authored per
 * shoal: a composition that contains a Cloaked fish teaches Cloaked, and no
 * amount of content editing can leave the flag behind on the wrong card.
 */

import { S } from '../content/strings.ts';
import { SPECIES } from '../sim/species.ts';
import type { ShoalGroup } from '../sim/types.ts';

export interface PropertyCard {
  id: string;
  name: string;
  /** The doc 06 marker, in the ASCII the pixel font actually has. */
  marker: string;
  rule: string;
  counter: string;
}

export const PROPERTIES: Record<string, PropertyCard> = {
  armored: {
    id: 'armored',
    name: S.props.armored,
    marker: '[#]',
    rule: S.props.flatDamageReduction,
    counter: S.props.bigHitsBeat,
  },
  swift: {
    id: 'swift',
    name: S.props.swift,
    marker: '[>]',
    rule: S.props.threeTilesSecond,
    counter: S.props.slowStunShove,
  },
  cloaked: {
    id: 'cloaked',
    name: S.props.cloaked,
    marker: '[?]',
    rule: S.props.noTowerCan,
    counter: S.props.coreLightsSix,
  },
  regen: {
    id: 'regen',
    name: S.props.regen,
    marker: '[+]',
    rule: S.props.healsEverySecond,
    counter: S.props.burstNotTrickle,
  },
};

/** Every property a shoal's roster teaches, in doc 06's introduction order. */
export function propertiesOf(composition: ShoalGroup[]): string[] {
  const out: string[] = [];
  for (const id of ['armored', 'swift', 'cloaked', 'regen']) {
    const carries = composition.some((g) => {
      const sp = SPECIES[g.species];
      if (!sp) return false;
      if (id === 'armored') return sp.armor > 0;
      if (id === 'swift') return sp.swift;
      if (id === 'cloaked') return sp.cloaked;
      return sp.regen > 0;
    });
    if (carries) out.push(id);
  }
  return out;
}
